
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, BarChart, Globe, Check } from "lucide-react";

export const ProductDemoSection = () => {
  const [isHovering, setIsHovering] = useState(false);
  
  const features = [
    {
      id: "dashboard",
      title: "Enterprise Dashboard",
      description: "Real-time analytics dashboard with customizable KPIs and executive reporting",
      icon: <BarChart className="w-5 h-5 text-sky-500" />,
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&h=450&q=80",
      highlights: [
        "Role-based access control",
        "Customizable widgets",
        "Export to Excel/PDF",
        "Automated reporting"
      ]
    },
    {
      id: "security",
      title: "Security Center",
      description: "Enterprise-grade security with audit logs and compliance reporting",
      icon: <Shield className="w-5 h-5 text-emerald-500" />,
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=800&h=450&q=80",
      highlights: [
        "SOC 2 compliance",
        "Encryption at rest & in transit",
        "Multi-factor authentication",
        "Detailed audit trails"
      ]
    },
    {
      id: "global",
      title: "Global Operations",
      description: "Manage cross-border operations with localized compliance handling",
      icon: <Globe className="w-5 h-5 text-sky-500" />,
      image: "https://images.unsplash.com/photo-1529310399831-ed472b81d589?auto=format&fit=crop&w=800&h=450&q=80",
      highlights: [
        "Multi-currency support",
        "Jurisdiction-specific compliance",
        "Global team management",
        "Localized reporting"
      ]
    }
  ];

  return (
    <section className="container mx-auto px-4 py-16 relative">
      
      <div className="relative">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900">Professional Solutions</h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto">Explore our enterprise-ready features designed to streamline operations and boost productivity.</p>
        </div>
        
        <Tabs defaultValue="dashboard" className="max-w-5xl mx-auto">
          <div className="flex justify-center mb-8">
            <TabsList className="bg-gray-50 backdrop-blur-sm p-1 h-auto">
              {features.map((feature) => (
                <TabsTrigger 
                  key={feature.id} 
                  value={feature.id}
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm px-6 py-3 rounded-lg flex items-center gap-2"
                >
                  {feature.icon}
                  {feature.title}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          
          {features.map((feature) => (
            <TabsContent key={feature.id} value={feature.id}>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
                <div className="lg:col-span-3 rounded-xl overflow-hidden shadow-md">
                  <div className="relative group"
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                  >
                    <img 
                      src={feature.image} 
                      alt={feature.title}
                      className="w-full h-auto transform transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                      width={800}
                      height={450}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-gray-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                      <div className="text-white">
                        <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                        <p>{feature.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="lg:col-span-2">
                  <Card className="bg-white/90 backdrop-blur-sm border-none shadow-md">
                    <CardContent className="p-6">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        {feature.icon}
                        <span>{feature.title}</span>
                      </h3>
                      <p className="text-gray-600 mb-6">{feature.description}</p>
                      
                      <h4 className="text-sm uppercase tracking-wider text-gray-500 font-semibold mb-3">Key Features</h4>
                      <ul className="space-y-3 mb-6">
                        {feature.highlights.map((highlight, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <div className="p-1 rounded-full bg-emerald-50 mt-0.5">
                              <Check className="w-3 h-3 text-emerald-500" />
                            </div>
                            <span className="text-gray-700">{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};
